/**
 * <shopla-local-inventory> — the trigger + popup of the "Product local inventory" block.
 *
 * The block is rendered entirely in Liquid (trigger status, per-location rows with availability
 * dot and address); this element adds the two dynamic parts:
 *
 * 1. The live "Open" / "Closed. Opens Monday at 09:00" line under each location, computed on the
 *    client from the location's `shopla.opening-hours` metafield JSON embedded per row — client-side
 *    for the same reason as <shopla-opening-hours>: Shopify caches the rendered page, so a
 *    server-side "now" would go stale. The schedule math (timezones, exceptions, next opening) is
 *    reused from ShoplaOpeningHours via a detached instance used purely as a calculator, so the two
 *    blocks can't drift apart — including how each row's timezone is resolved: the location's own
 *    `timezone`, else the one its `shopla.country-code` implies. A row whose timezone neither
 *    settles hides its open-state line, since its wall clock is unknown; it must not fall back to
 *    the STORE's UTC offset the way it used to, which is the merchant admin's timezone and reported
 *    a Zürich shop open six hours past closing.
 *
 * 2. Re-rendering on variant change: availability is variant-scoped (the `shopla.shop-locations`
 *    variant metafield and the native quantity), so on `productSelect` the freshly rendered block
 *    is morphed in, mirroring product-inventory.js.
 */

import { morph } from '@theme/morph';
import { StandardEvents } from '@shopify/events';
import { ShoplaOpeningHours, resolveTimezone } from '@theme/shopla-opening-hours';

const UPDATE_INTERVAL_MS = 60 * 1000;

class ShoplaLocalInventory extends HTMLElement {
  connectedCallback() {
    this.updateOpenStates();
    this.timer = window.setInterval(() => this.updateOpenStates(), UPDATE_INTERVAL_MS);
    this.listenTarget = this.closest('.shopify-section, dialog');
    this.listenTarget?.addEventListener(StandardEvents.productSelect, this.handleProductSelect);
  }

  disconnectedCallback() {
    if (this.timer) window.clearInterval(this.timer);
    this.listenTarget?.removeEventListener(StandardEvents.productSelect, this.handleProductSelect);
    this.listenTarget = null;
  }

  /**
   * Swaps in the re-rendered block when another variant is selected. Mirrors product-inventory.js;
   * the open-state lines are re-filled afterwards because the fresh server HTML ships them empty.
   * @param {CustomEvent} event - The product select event.
   */
  handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const fresh = html.querySelector(`shopla-local-inventory[data-block-id="${this.dataset.blockId}"]`);
        if (!fresh) return;

        morph(this, fresh, { childrenOnly: true });
        this.updateOpenStates();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[shopla-local-inventory] Event promise rejected:', error);
      });
  };

  /**
   * A detached <shopla-opening-hours> configured as a pure schedule calculator (never connected,
   * so its own rendering lifecycle never runs). Created via document.createElement so we always
   * get the registered implementation.
   * @param {object} openingHours - Parsed `shopla.opening-hours` metafield value.
   * @param {string | undefined} countryCode - The row location's ISO country code.
   */
  calculatorFor(openingHours, countryCode) {
    const calculator = /** @type {ShoplaOpeningHours} */ (document.createElement('shopla-opening-hours'));
    calculator.data = openingHours;
    calculator.timezone = resolveTimezone(openingHours, countryCode);
    calculator.locale = this.dataset.locale || document.documentElement.lang || undefined;
    calculator.use12Hour = this.dataset.timeFormat === '12';
    return calculator;
  }

  /**
   * Fills the open-state line of every location row: the colored "Open"/"Closed" state word and —
   * only when closed — when the store opens next ("Opens at 09:00" / "Opens Monday at 09:00").
   * Rows without usable opening hours — or without a timezone to read them in — stay hidden.
   */
  updateOpenStates() {
    const labels = {
      open: this.dataset.labelOpen || 'Open',
      closed: this.dataset.labelClosed || 'Closed',
      opensAt: this.dataset.labelOpensAt || 'Opens at {time}',
      opensOn: this.dataset.labelOpensOn || 'Opens {day} at {time}',
    };

    for (const line of this.querySelectorAll('.shopla-local-inventory__open-state')) {
      let openingHours = null;
      try {
        const parsed = JSON.parse(line.dataset.openingHours || '{}');
        if (parsed && typeof parsed === 'object') openingHours = parsed;
      } catch (error) {
        openingHours = null;
      }

      const stateEl = line.querySelector('.shopla-local-inventory__oh-state');
      const detailEl = line.querySelector('.shopla-local-inventory__oh-detail');
      const calculator = openingHours ? this.calculatorFor(openingHours, line.dataset.countryCode) : null;

      if (!stateEl || !detailEl || !calculator?.hasAnyHours()) {
        line.hidden = true;
        continue;
      }

      // No timezone, no wall clock to compare the hours against — say nothing rather than guess.
      const moment = calculator.currentMoment();
      if (!moment) {
        line.hidden = true;
        continue;
      }

      const { isoDate, minutes, weekdayKey } = moment;
      const schedule = calculator.scheduleFor(isoDate, weekdayKey);
      const open = ShoplaOpeningHours.isOpenAt(schedule, minutes);

      stateEl.textContent = open ? labels.open : labels.closed;

      let detail = '';
      if (!open) {
        const next = calculator.nextOpening(isoDate, minutes);
        if (next) {
          detail =
            next.dayOffset === 0
              ? ShoplaOpeningHours.template(labels.opensAt, { time: calculator.minutesToTime(next.opens) })
              : ShoplaOpeningHours.template(labels.opensOn, {
                  day: calculator.weekdayName(next.isoDate),
                  time: calculator.minutesToTime(next.opens),
                });
          detail = `. ${detail}`;
        }
      }
      detailEl.textContent = detail;

      line.classList.toggle('is-open', open);
      line.classList.toggle('is-closed', !open);
      line.hidden = false;
    }
  }
}

if (!customElements.get('shopla-local-inventory')) {
  customElements.define('shopla-local-inventory', ShoplaLocalInventory);
}
